
    import { defineComponent, computed, ref, onMounted, onUnmounted, watch } from 'vue'
    import { Route } from '../enums/Route'
    import CustomCursor from './CustomCursor.vue'
    import ClippingAnimation from './ClippingAnimation.vue'

    export default defineComponent({
        name: 'HeaderNew',
        components: {
            CustomCursor,
            ClippingAnimation
        },
        setup() {

            const isHeaderHidden = ref(false)
            const activeMegaMenu = ref<'solutions' | 'capabilities' | null>(null)
            const isMegaMenuRevealed = ref(false)
            const isMobileMenuOpen = ref(false)
            const activeMobileSubmenu = ref<'solutions' | 'capabilities' | null>(null)
            let originalBodyOverflow = ''
            let originalBodyPosition = ''
            let originalBodyTop = ''
            let originalBodyWidth = ''
            let originalHtmlOverflow = ''
            let lockedScrollY = 0
            let megaMenuRevealTimer = 0
            const solutionMenuItems = [
                {
                    title: 'Platforms',
                    description: 'Enterprise portals, dashboards, and CRM solutions that connect teams, systems, data, and operations.'
                },
                {
                    title: 'Products',
                    description: 'Mobile apps, mini programs, SaaS platforms, and connected digital products for modern businesses.'
                },
                {
                    title: 'Commerce',
                    description: 'Commerce experiences across WeChat, mobile, and web for loyalty, retail, and China ecosystems.'
                },
                {
                    title: 'AI',
                    description: 'AI-powered products, automation, assistants, and intelligent business workflows that scale.'
                },
                {
                    title: 'Infrastructure',
                    description: 'Integrations, APIs, architecture, and modernization for complex enterprise technology ecosystems.'
                },
                {
                    title: 'China Tech',
                    description: 'Digital solutions designed for China\'s unique technology landscape and regulations.'
                }
            ]
            const capabilityMenuItems = [
                {
                    title: 'Strategy',
                    description: 'Align business goals & roadmaps'
                },
                {
                    title: 'Product Design',
                    description: 'Digital products, interfaces & experiences'
                },
                {
                    title: 'User Research',
                    description: 'Evidence-based user insights'
                },
                {
                    title: 'Engineering',
                    description: 'Mobile, WeChat & enterprise apps'
                },
                {
                    title: 'AI & Data',
                    description: 'AI-powered products, automation & insights'
                },
                {
                    title: 'Integration & Architecture',
                    description: 'APIs, integrations & technical foundations'
                },
                {
                    title: 'Security & Compliance',
                    description: 'Secure systems, governance & compliance'
                },
                {
                    title: 'Managed Services',
                    description: 'Support, maintenance & optimisation'
                }
            ]
            const menuProjects = {
                solutions: {
                    image: '/assets/projects/event-companion.png',
                    title: 'Event Companion',
                    client: 'Logitech',
                    type: 'Desktop App'
                },
                capabilities: {
                    image: '/assets/projects/ai-portal.png',
                    title: 'Options+ AI Portal',
                    client: 'Logitech',
                    type: 'Desktop App'
                }
            }
            let lastScrollY = 0
            const scrollThreshold = 10

            const activeMegaMenuItems = computed(() => {

                if (activeMegaMenu.value === 'capabilities') {

                    return capabilityMenuItems

                }

                return solutionMenuItems

            })

            const activeMegaMenuProject = computed(() => {

                if (activeMegaMenu.value === 'capabilities') {

                    return menuProjects.capabilities

                }

                return menuProjects.solutions

            })

            const activeMobileSubmenuItems = computed(() => {

                if (activeMobileSubmenu.value === 'capabilities') {

                    return capabilityMenuItems

                }

                return solutionMenuItems

            })

            const activeMobileSubmenuProject = computed(() => {

                if (activeMobileSubmenu.value === 'capabilities') {

                    return menuProjects.capabilities

                }

                return menuProjects.solutions

            })

            const openMegaMenu = (menu: 'solutions' | 'capabilities') => {

                if (activeMegaMenu.value === menu && isMegaMenuRevealed.value) {

                    return

                }

                isMegaMenuRevealed.value = false
                activeMegaMenu.value = menu

                window.clearTimeout(megaMenuRevealTimer)
                megaMenuRevealTimer = window.setTimeout(() => {

                    if (activeMegaMenu.value === menu) {

                        isMegaMenuRevealed.value = true

                    }

                }, 80)

            }

            const closeMegaMenu = () => {

                window.clearTimeout(megaMenuRevealTimer)
                isMegaMenuRevealed.value = false
                activeMegaMenu.value = null

            }

            const toggleMobileMenu = () => {

                isMobileMenuOpen.value = !isMobileMenuOpen.value

                if (!isMobileMenuOpen.value) {

                    activeMobileSubmenu.value = null

                }

            }

            const openMobileSubmenu = (menu: 'solutions' | 'capabilities') => {

                activeMobileSubmenu.value = menu

            }

            const closeMobileSubmenu = () => {

                activeMobileSubmenu.value = null

            }

            const syncBodyScrollLock = () => {

                if (typeof document === 'undefined') {

                    return

                }

                const shouldLockScroll = Boolean(activeMegaMenu.value || isMobileMenuOpen.value)

                if (shouldLockScroll) {

                    if (document.body.style.position !== 'fixed') {

                        lockedScrollY = window.scrollY

                    }

                    document.documentElement.style.overflow = 'hidden'
                    document.body.style.overflow = 'hidden'
                    document.body.style.position = 'fixed'
                    document.body.style.top = `-${ lockedScrollY }px`
                    document.body.style.width = '100%'

                    return

                }

                document.documentElement.style.overflow = originalHtmlOverflow
                document.body.style.overflow = originalBodyOverflow
                document.body.style.position = originalBodyPosition
                document.body.style.top = originalBodyTop
                document.body.style.width = originalBodyWidth
                window.scrollTo(0, lockedScrollY)

            }

            const handleScroll = () => {

                if (activeMegaMenu.value || isMobileMenuOpen.value) {

                    return

                }

                const currentScrollY = window.scrollY
                activeMegaMenu.value = null

                if (currentScrollY < scrollThreshold) {

                    isHeaderHidden.value = false

                } else if (currentScrollY > lastScrollY) {

                    isHeaderHidden.value = true

                } else {

                    isHeaderHidden.value = false

                }

                lastScrollY = currentScrollY

            }

            onMounted(() => {

                originalHtmlOverflow = document.documentElement.style.overflow
                originalBodyOverflow = document.body.style.overflow
                originalBodyPosition = document.body.style.position
                originalBodyTop = document.body.style.top
                originalBodyWidth = document.body.style.width
                window.addEventListener('scroll', handleScroll, { passive: true })

            })

            watch([ activeMegaMenu, isMobileMenuOpen ], syncBodyScrollLock)

            onUnmounted(() => {

                window.clearTimeout(megaMenuRevealTimer)
                window.removeEventListener('scroll', handleScroll)
                document.documentElement.style.overflow = originalHtmlOverflow
                document.body.style.overflow = originalBodyOverflow
                document.body.style.position = originalBodyPosition
                document.body.style.top = originalBodyTop
                document.body.style.width = originalBodyWidth

            })

            return {
                Route,
                isHeaderHidden,
                activeMegaMenu,
                isMegaMenuRevealed,
                activeMegaMenuItems,
                activeMegaMenuProject,
                openMegaMenu,
                closeMegaMenu,
                isMobileMenuOpen,
                activeMobileSubmenu,
                activeMobileSubmenuItems,
                activeMobileSubmenuProject,
                toggleMobileMenu,
                openMobileSubmenu,
                closeMobileSubmenu
            }

        }
    })
